<script lang="ts" setup>
//=======================================
//              Imports
//=======================================
import { useI18n } from "vue-i18n"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { computed, ref, watch } from "vue"
import { VARIABLES } from "@modules/energy-manager/constants"
import { formatNumber } from "@core/helpers"
import { ServiceError } from "@core/exceptions/ServiceError"
import moment from "moment"

//=======================================
//              Variables
//=======================================
const visible = ref<boolean>(false)
const emConsoCompareStore = useEmConsoCompareStore()
const options = ref([
    {
        label: "+ Élevés",
        value: true,
    },
    {
        label: "- Élevés",
        value: false,
    },
])
const optionRankingSelected = ref<boolean>(true)

//======================================
//                Lang
//======================================
const { t } = useI18n({
    messages: {
        fr: {
            title: () => {
                const value = optionRankingSelected.value
                return value ? "+ élevés" : "- élevés"
            },
            sub_title: "Variable de comparaison : { variable } ({ unit })",
            messageInfo:
                "Ce classement est basé sur la variable {variable} sélectionnée ci-dessus.",
            kpi: {
                title: "#{index} : {name}",
                variable: "{name} en {unit}",
                value: "{value} {unit}",
            },
            tooltip: "Accéder au bâtiment.",
            help: "Que signifie ce classement ?",
            helpMessage: "Affiche les bâtiments avec {value} ",
            empty: "Il n'y a aucun bâtiment dans le classement.",
            error: {
                title: ({
                    named,
                }: {
                    named: (key: string) => string | string[]
                }) => {
                    const code = named("code") as string
                    const mappingCode = {
                        "aucune-donnee": `Impossible de lancer l'analyse`,
                    } as Record<string, string>

                    return mappingCode?.[code] ?? "Erreur"
                },
                detail: ({
                    named,
                }: {
                    named: (key: string) => string | string[]
                }) => {
                    const code = named("code") as string
                    const period = named("period") as string[]
                    const variable = named("variable") as string
                    const reason = named("reason") as string
                    const mappingCode = {
                        "aucune-donnee": `Aucune donnée ${variable} trouvée sur la période entre le ${period[0]} et le ${period[1]}. Éssayer sur une autre période ou vérifier ${reason}.`,
                    } as Record<string, string>

                    return (
                        mappingCode?.[code] ??
                        "Une erreur est survenue lors de la récupération des données"
                    )
                },
            },
            exemple:
                "Ce classement suit la variable définie dans les paramètres en haut de la page. Modifiez-la pour changer le critère de tri.",
        },
    },
})

//======================================
//              Functions
//======================================
/**
 * Renvois le nom du bâtiment correspondant à l'ID donné.
 * @param id
 */
function getBuildingNameById(id: string): string {
    const building = emConsoCompareStore.buildings.find(
        (b) => b._id_building === id
    )
    return building?.name || "Inconnu"
}
/**
 * Renvois l'adresse du bâtiment correspondant à l'ID donné.
 * @param id
 */
function getBuildingAdressById(id: string): string {
    const building = emConsoCompareStore.buildings.find(
        (b) => b._id_building === id
    )
    return building?.address.inline_name || " Adresse non trouvée"
}

/**
 * Indique si la valeur peut être utilisée pour le classement Top/Flop.
 * Exclut null, undefined, NaN et Infinity ; conserve 0.
 * @param value - Valeur brute issue de compareData
 */
function isRankingValue(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value)
}

//===============================================================================
//                                  Computed
//===============================================================================
/**
 * Classe les bâtiments en - élevés ou + élevés en triant compareData.
 */
const getTopFlop = computed(() => {
    if (!emConsoCompareStore.compareData) return []

    const variableKey = getVariableKey.value
    if (!variableKey) return []
    const filteredBuildingsIds = new Set(
        emConsoCompareStore.snapshotCompareBody?._id_buildings
            .map((_id_building: any) =>
                emConsoCompareStore.getComparedBuildingsList.find(
                    (b: any) => b._id_building === _id_building
                )
            )
            .filter((building: any) => !!building)
            .filter((building: any) => {
                const validSources = building.sources.filter(
                    (source: any) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                )

                if (
                    [
                        "energie_tout_tout",
                        "energiepardju_tout_tout",
                        "eqco2_tout_tout",
                        "cout_tout_tout",
                    ].includes(variableKey)
                ) {
                    return validSources.some((source: any) =>
                        ["pdl", "pce"].includes(source.idtype)
                    )
                }

                if (
                    [
                        "energie_elec_tout",
                        "energiepardju_elec_tout",
                        "eqco2_elec_tout",
                        "cout_elec_tout",
                        "energie_elecinjection_tout",
                    ].includes(variableKey)
                ) {
                    return validSources.some(
                        (source: any) => source.idtype === "pdl"
                    )
                }

                if (
                    [
                        "energie_gaz_tout",
                        "energiepardju_gaz_tout",
                        "eqco2_gaz_tout",
                        "cout_gaz_tout",
                    ].includes(variableKey)
                ) {
                    return validSources.some(
                        (source: any) => source.idtype === "pce"
                    )
                }

                if (variableKey === "temperature_air_interieur") {
                    return validSources.some(
                        (source: any) =>
                            ![
                                "pdl",
                                "pce",
                                "idopenmeteo",
                                "idmeteofrance",
                            ].includes(source.idtype)
                    )
                }

                return true
            })
            .map((building: any) => building?._id_building)
    )

    const filteredData = Object.entries(
        emConsoCompareStore.compareData?.[
            emConsoCompareStore.compareData.length > 1 ? 1 : 0
        ] ?? {}
    )
        .filter(([buildingId]) => filteredBuildingsIds.has(buildingId))
        .filter(([, value]) => isRankingValue(value)) as [string, number][]

    const sorted = filteredData.sort((a, b) => b[1] - a[1])

    const topList = sorted
        .slice(0, 3)
        .map(([id, value]) => ({ _id_building: id, value }))
    const flopList = sorted
        .slice(-3)
        .reverse()
        .map(([id, value]) => ({ _id_building: id, value }))
        // filtre les bâtiments déjà dans le top
        ?.filter(
            (building) =>
                !topList.some(
                    (top) => top._id_building === building._id_building
                )
        )

    return optionRankingSelected.value ? topList : flopList
})

const getVariable = computed(() => {
    if (!emConsoCompareStore.compareVariable) return null

    const splitVariable = emConsoCompareStore.compareVariable.split(".")[0]
    const maxDigit = parseInt(
        VARIABLES[splitVariable].format?.match(/\d+/)?.[0] ?? "1"
    )

    return {
        ...VARIABLES[splitVariable],
        maxDigit,
    }
})

const getVariableKey = computed(() => {
    if (!emConsoCompareStore.compareVariable) return null
    return emConsoCompareStore.compareVariable.split(".")[0]
})
//======================================
//               onEvents
//======================================

function navigateToBuilding(id: string) {
    emConsoCompareStore.clickLink(id, "building")
}

// =====================================================
// Gestion du click sur les cellules
// =====================================================
// référence vers le menu contextuel
const menu = ref<any>(null)
// information de la cellule sélectionnée pour le menu contextuel
const selectedBuilding = ref<string>("")
/**
 * Récupère le menu contextuel
 */
const getContextMenu = computed(() => {
    return [
        {
            label: "Ouvrir dans un autre onglet",
            icon: "pi pi-external-link",
            command: () => {
                emConsoCompareStore.clickLink(
                    selectedBuilding.value,
                    "building",
                    true
                )
            },
        },
    ]
})

/**
 * Quand le menu contextuel doit s'ouvrir
 * @param event - Event de clique
 * @param data - Donnée de la cellule
 * @param field - Nom du champ
 * @param index - Index de la ligne
 */
function onClickOpenMenu(event: any, _id_building: string) {
    // On stocke les informations de la cellule sélectionnée
    selectedBuilding.value = _id_building
    if (menu.value) {
        menu.value.show(event)
    }
}

// ============================================
// Gestion des erreurs liés à la comparaison
// ============================================
const errorMessage = ref<{ title: string; content: string } | null>(null)
watch(
    () => emConsoCompareStore.fetchCompareGraphByBuildingIds.error,
    (error) => {
        if (error) {
            const serviceError = error as ServiceError
            const variableKey =
                emConsoCompareStore.snapshotCompareBody?.variable?.split(
                    "."
                )?.[0] ?? ""
            const periods =
                emConsoCompareStore.snapshotCompareBody?.periods ?? []
            errorMessage.value = {
                title: t("error.title", {
                    code: serviceError?.code,
                }),
                content: t("error.detail", {
                    code: serviceError?.code,
                    period: [
                        moment(periods[periods?.length - 1]?.[0]).format(
                            "DD/MM/YYYY"
                        ),
                        moment(periods[periods?.length - 1]?.[1]).format(
                            "DD/MM/YYYY"
                        ),
                    ],
                    reason: t(`variables.${variableKey}.empty_data`),
                    variable: t(`variables.${variableKey}.sentence_2`),
                }),
            }
        } else {
            errorMessage.value = null
        }
    }
)

/**
 * Vérifie si la route de la comparaison a remontée une erreur
 */
const hasErrorNoData = computed(() => {
    // Vérifie si une erreur au code "aucune-donnee" est présente
    return !!emConsoCompareStore.fetchCompareGraphByBuildingIds.error
})
</script>

<template>
    <BaseCard class="card-top-flop">
        <template #header>
            <!-- En tête -->
            <div class="card-top-flop__header">
                <div class="card-top-flop__header-title">
                    <h1 class="font-bold text-xl text-primary-700">
                        {{ t("title", { value: optionRankingSelected }) }}
                    </h1>
                    <i
                        class="pi pi-info-circle text-primary-400"
                        v-tooltip="t('messageInfo')"
                    ></i>
                </div>
                <!-- Top/Flop sélecteur -->
                <BaseSelectButton
                    v-model="optionRankingSelected"
                    :options="options"
                    :allow-empty="false"
                    option-label="label"
                    option-value="value"
                    class="card-top-flop__header-select"
                ></BaseSelectButton>
            </div>
        </template>
        <template #content>
            <!-- Liste des top/flop -->
            <div
                class="card-top-flop__list py-2"
                v-if="
                    getTopFlop.length >= 0 &&
                    !emConsoCompareStore.fetchCompareGraphByBuildingIds
                        .isFetching
                "
            >
                <div
                    v-for="(item, index) in getTopFlop"
                    :key="index"
                    class="card-top-flop__list-item"
                    @click="navigateToBuilding(item._id_building)"
                    @contextmenu="onClickOpenMenu($event, item._id_building)"
                >
                    <!-- Liste -->
                    <div class="flex items-center gap-1">
                        <BaseIcon
                            v-for="n in 3"
                            :key="n"
                            :icon="
                                getVariable?.ranking_order !==
                                (optionRankingSelected ? 'asc' : 'desc')
                                    ? 3 - n >= index
                                        ? 'fa-regular fa-thumbs-down'
                                        : 'fa-light fa-thumbs-down'
                                    : 3 - n >= index
                                      ? 'fa-regular fa-thumbs-up'
                                      : 'fa-light fa-thumbs-up'
                            "
                            :style="{
                                color:
                                    getVariable?.ranking_order !==
                                    (optionRankingSelected ? 'asc' : 'desc')
                                        ? 3 - n >= index
                                            ? '#FF3D32'
                                            : '#E0E0E0'
                                        : 3 - n >= index
                                          ? '#22c55e'
                                          : '#E0E0E0',

                                marginRight: '0.25rem',
                                fontSize: '1.5em',
                            }"
                        />
                    </div>
                    <div class="card-top-flop__list-item-text">
                        <h2
                            v-tooltip.left="{
                                value: t('tooltip'),
                                showDelay: 1500,
                            }"
                            class="font-bold text-xl text-primary-700 max-w-xs truncate overflow-hidden whitespace-nowrap"
                        >
                            {{ getBuildingNameById(item._id_building) }}
                        </h2>
                        <p
                            v-tooltip.left="{
                                value: t('tooltip'),
                                showDelay: 1500,
                            }"
                            class="text-primary-700 max-w-xs truncate overflow-hidden whitespace-nowrap"
                        >
                            {{ getBuildingAdressById(item._id_building) }}
                        </p>
                    </div>
                    <!-- Icon -->
                    <BaseIndicatorItem
                        class="card-top-flop__list-item-kpi"
                        :name="getVariable?.name"
                        :icon="getVariable?.icon"
                        :unit="getVariable?.unit"
                        :icon-color="getVariable?.iconColor"
                        :value="
                            formatNumber(item.value, {
                                style:
                                    getVariable?.unit === '€'
                                        ? 'currency'
                                        : 'decimal',
                                maximumFractionDigits: getVariable?.maxDigit,
                            })!
                        "
                        :loading="false"
                    ></BaseIndicatorItem>
                </div>
                <BaseAppMessage v-if="getTopFlop.length === 0">
                    {{ t("empty") }}
                </BaseAppMessage>
            </div>
            <!-- Liste des top/flop en chargement -->
            <div
                v-else
                class="card-top-flop__list card-top-flop__list--loading"
            >
                <BaseSkeleton
                    v-for="skeleton in 3"
                    :key="skeleton"
                    height="14.375rem"
                />
                <BaseAppMessage
                    v-if="hasErrorNoData"
                    severity="error"
                    class="card-top-flop__error-message"
                >
                    <template #header>
                        {{ errorMessage?.title }}
                    </template>
                    {{ errorMessage?.content }}
                </BaseAppMessage>
            </div>
            <!-- Bouton aide -->
            <BaseButton
                icon="pi pi-question-circle"
                size="small"
                variant="link"
                :label="t('help')"
                severity="secondary"
                aria-label="Afficher l'aide"
                @click="visible = true"
            />
            <!-- Dialog d'aide -->
            <BaseDialog
                v-model:visible="visible"
                modal
                :header="t('help')"
                :style="{ maxWidth: '50rem' }"
            >
                <div class="card-top-flop__list-item-help-message">
                    {{ t(`variables.${getVariableKey}.top_flop`) }}
                    <div class="card-top-flop__list-item-help-message-screen">
                        {{ t("exemple") }}
                        <img
                            src="@modules/energy-manager/assets/img/explain_variable_select.png"
                            class="w-[300px] shadow-lg rounded-lg mx-auto"
                            width="300"
                            height="auto"
                        />
                    </div>
                </div>
            </BaseDialog>
            <BaseContextMenu ref="menu" :model="getContextMenu" />
        </template>
    </BaseCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
/* Bloc des top/flop */
.card-top-flop {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    position: relative;

    padding: 1.5rem;
    border-width: 1px;
    --p-card-shadow: none;
    --p-card-body-padding: none;
}
/* En tête */
.card-top-flop__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;

    & .card-top-flop__header-title {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        align-items: center;
    }

    .card-top-flop__header-select {
        margin-left: auto;
    }
}
/* Liste des top/flop */
.card-top-flop__list {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    padding-bottom: 1rem;
    position: relative;

    padding-bottom: 1rem;
    overflow-x: auto;
}

.card-top-flop__list--loading {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}

/* Carte */
.card-top-flop__list-item {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    border-width: 1px;
    border-color: var(--p-surface-300);
    border-radius: 6px;
    padding: 1rem;
    width: 100%;
    &:hover {
        border-color: var(--p-primary-600);
        h2,
        p {
            &:hover {
                text-decoration: underline;
            }
        }
    }

    .card-top-flop__list-item-text {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
    .card-top-flop__list-item-kpi {
        .card-top-flop__list-item:hover & {
            border-color: var(--p-primary-600);
        }
    }
    .card-top-flop__list-item-kpi-content {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
    .card-top-flop__list-item-waiting-message {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1000;
    }
}

.card-top-flop__error-message {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.card-top-flop__list-item-help-message {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    & .card-top-flop__list-item-help-message-screen {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
}
</style>
